import { useEffect } from "react";

const SCREENSHOT_QUERY_KEY = "screenshot";
const PDF_QUERY_KEY = "pdf";
const NETWORK_IDLE_MS = 800;
const NETWORK_TIMEOUT_MS = 15000;
const MAX_SCROLL_PASSES = 2;

function getSearchParams() {
  if (typeof window === "undefined") return new URLSearchParams();
  return new URLSearchParams(window.location.search);
}

export function isScreenshotMode() {
  return getSearchParams().get(SCREENSHOT_QUERY_KEY) === "true";
}

export function isPdfMode() {
  return getSearchParams().get(PDF_QUERY_KEY) === "true";
}

export function isRenderPrepMode() {
  return isScreenshotMode() || isPdfMode();
}

function ensureNetworkTracking() {
  if (typeof window === "undefined") return;
  if (window.__RENDER_NETWORK_TRACKING__) return;

  window.__RENDER_NETWORK_TRACKING__ = true;
  window.__RENDER_PENDING_REQUESTS__ = 0;

  const bump = (delta) => {
    window.__RENDER_PENDING_REQUESTS__ = Math.max(
      0,
      (window.__RENDER_PENDING_REQUESTS__ || 0) + delta
    );
  };

  const originalFetch = window.fetch.bind(window);
  window.fetch = (...args) => {
    bump(1);
    return originalFetch(...args).finally(() => bump(-1));
  };

  const OriginalXhrOpen = window.XMLHttpRequest.prototype.open;
  const OriginalXhrSend = window.XMLHttpRequest.prototype.send;

  window.XMLHttpRequest.prototype.open = function patchedOpen(...args) {
    this.__renderTracked__ = true;
    return OriginalXhrOpen.apply(this, args);
  };

  window.XMLHttpRequest.prototype.send = function patchedSend(...args) {
    if (this.__renderTracked__) {
      bump(1);
      this.addEventListener("loadend", () => bump(-1), { once: true });
    }
    return OriginalXhrSend.apply(this, args);
  };
}

function disableMotionAndTransitions() {
  const styleId = "render-mode-disable-motion";
  if (document.getElementById(styleId)) return;

  const style = document.createElement("style");
  style.id = styleId;
  style.textContent = `
    html[data-render-mode="true"], body[data-render-mode="true"] {
      scroll-behavior: auto !important;
    }
    html[data-render-mode="true"] *,
    body[data-render-mode="true"] * {
      animation: none !important;
      transition: none !important;
      caret-color: transparent !important;
    }
  `;
  document.head.appendChild(style);
}

function eagerlyLoadMedia(root = document) {
  const images = root.querySelectorAll("img");
  images.forEach((img) => {
    img.loading = "eager";
    img.decoding = "sync";
    img.setAttribute("fetchpriority", "high");
  });

  const iframes = root.querySelectorAll("iframe");
  iframes.forEach((frame) => {
    frame.loading = "eager";
  });

  const videos = root.querySelectorAll("video");
  videos.forEach((video) => {
    video.preload = "auto";
    try {
      video.load();
    } catch (_) {
      // Best effort only.
    }
  });
}

function observeNewMediaNodes() {
  const observer = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        eagerlyLoadMedia(node);
      });
    });
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  return observer;
}

function waitForAllImages(timeoutMs = NETWORK_TIMEOUT_MS) {
  const imgs = Array.from(document.images || []);
  if (imgs.length === 0) return Promise.resolve();

  const imagePromises = imgs.map((img) => {
    if (img.complete) return Promise.resolve();
    return new Promise((resolve) => {
      const done = () => resolve();
      img.addEventListener("load", done, { once: true });
      img.addEventListener("error", done, { once: true });
    });
  });

  return Promise.race([
    Promise.all(imagePromises),
    new Promise((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
}

function waitForNetworkIdle(idleMs = NETWORK_IDLE_MS, timeoutMs = NETWORK_TIMEOUT_MS) {
  const start = Date.now();

  return new Promise((resolve) => {
    const tick = () => {
      if ((window.__RENDER_PENDING_REQUESTS__ || 0) === 0) {
        setTimeout(() => {
          if ((window.__RENDER_PENDING_REQUESTS__ || 0) === 0) {
            resolve();
          } else {
            tick();
          }
        }, idleMs);
        return;
      }

      if (Date.now() - start > timeoutMs) {
        resolve();
        return;
      }

      setTimeout(tick, 150);
    };

    tick();
  });
}

function waitForDocumentComplete(timeoutMs = NETWORK_TIMEOUT_MS) {
  if (document.readyState === "complete") return Promise.resolve();

  return new Promise((resolve) => {
    const done = () => {
      window.removeEventListener("load", done);
      resolve();
    };

    window.addEventListener("load", done, { once: true });
    setTimeout(done, timeoutMs);
  });
}

async function autoScrollToRenderAll() {
  const viewport = window.innerHeight || 900;
  const pause = 100;

  for (let pass = 0; pass < MAX_SCROLL_PASSES; pass += 1) {
    let currentY = 0;
    const maxY = Math.max(
      0,
      document.documentElement.scrollHeight - viewport
    );

    while (currentY < maxY) {
      currentY = Math.min(currentY + Math.floor(viewport * 0.75), maxY);
      window.scrollTo(0, currentY);
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, pause));
    }
  }

  window.scrollTo(0, 0);
}

export function useRenderModePreparation(enabled, routeKey) {
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;

    const screenshotMode = isScreenshotMode();
    const pdfMode = isPdfMode();

    window.__RENDER_MODE__ = true;
    window.__RENDER_VIRTUALIZATION_DISABLED__ = true;
    window.__RENDER_READY__ = false;
    window.__SCREENSHOT_MODE__ = screenshotMode;
    window.__PDF_MODE__ = pdfMode;
    window.__SCREENSHOT_VIRTUALIZATION_DISABLED__ = screenshotMode;
    window.__PDF_VIRTUALIZATION_DISABLED__ = pdfMode;
    window.__SCREENSHOT_READY__ = false;
    window.__PDF_READY__ = false;

    document.documentElement.setAttribute("data-render-mode", "true");
    document.body.setAttribute("data-render-mode", "true");
    if (screenshotMode) {
      document.documentElement.setAttribute("data-screenshot-mode", "true");
      document.body.setAttribute("data-screenshot-mode", "true");
    }
    if (pdfMode) {
      document.documentElement.setAttribute("data-pdf-mode", "true");
      document.body.setAttribute("data-pdf-mode", "true");
    }

    ensureNetworkTracking();
    disableMotionAndTransitions();
    eagerlyLoadMedia();
    const observer = observeNewMediaNodes();

    let cancelled = false;
    const prepare = async () => {
      await waitForDocumentComplete();
      await waitForNetworkIdle();
      await waitForAllImages();
      await autoScrollToRenderAll();
      await waitForNetworkIdle();
      await waitForAllImages();
      await new Promise((resolve) => setTimeout(resolve, 500));

      if (!cancelled) {
        window.__RENDER_READY__ = true;
        window.__SCREENSHOT_READY__ = screenshotMode;
        window.__PDF_READY__ = pdfMode;
        window.dispatchEvent(new Event("render-ready"));
        if (screenshotMode) {
          window.dispatchEvent(new Event("screenshot-ready"));
        }
        if (pdfMode) {
          window.dispatchEvent(new Event("pdf-ready"));
        }
      }
    };

    prepare();

    return () => {
      cancelled = true;
      observer.disconnect();
      window.__RENDER_READY__ = false;
      window.__SCREENSHOT_READY__ = false;
      window.__PDF_READY__ = false;
    };
  }, [enabled, routeKey]);
}

// Backward-compatible alias used by existing code.
export function useScreenshotModePreparation(enabled, routeKey) {
  useRenderModePreparation(enabled, routeKey);
}

